import { useState, useContext } from "react";
import {
  Card,
  Typography,
  Radio,
  Input,
  Button,
  Modal,
  Table,
  Spin,
  DatePicker,
} from "antd";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { useEffect } from "react";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Resignation() {
  const { authData, backUrl } = useContext(AuthContext);
  const [reason, setReason] = useState("");
  const [willServeNotice, setWillServeNotice] = useState("yes");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // Withdraw modal
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  // Waiver modal
  const [waiverModalOpen, setWaiverModalOpen] = useState(false);
  const [waiverReason, setWaiverReason] = useState("");
  const [waiverDate, setWaiverDate] = useState(null);
  const [waiverLoading, setWaiverLoading] = useState(false);

  // Fetch resignation records
  const fetchMyRequests = async () => {
    try {
      setTableLoading(true);
      const res = await axios.get(
        `${backUrl}/api/payroll/resignations/my-requests/`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setRecords(res.data.results || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your resignation requests.");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (willServeNotice === "yes") {
      const autoDate = dayjs().add(30, "day");
      setWaiverDate(autoDate);
    } else {
      setWaiverDate(null);
    }
  }, [willServeNotice]);

  // Submit resignation
  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.warn("Please enter your resignation reason.");
      return;
    }

    const payload = {
      reason: reason.trim(),
      is_waiver_requested: willServeNotice === "no",
      requested_last_working_day: dayjs(waiverDate).format("YYYY-MM-DD"),
    };


    try {
      setLoading(true);
      await axios.post(`${backUrl}/api/payroll/resignations/create/`, payload, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Resignation submitted successfully!");
      setReason("");
      setWillServeNotice("yes");
    } catch (err) {
      console.error(err);

      const errorMsg =
        err?.response?.data?.data ||
        err?.response?.data?.message ||
        "Failed to submit resignation.";

      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    fetchMyRequests();
  };

  // Handle withdraw
  const handleWithdraw = (record) => {
    setSelectedRequest(record);
    setWithdrawModalOpen(true);
  };

  const submitWithdraw = async () => {
    if (!withdrawReason.trim()) {
      toast.warn("Please provide a reason for withdrawal.");
      return;
    }

    const payload = {
      reason: withdrawReason,
      employee: authData?.employee?.id,
      resignation_request: selectedRequest.id,
    };

    try {
      setWithdrawLoading(true);
      await axios.post(
        `${backUrl}/api/payroll/resignations/${selectedRequest.id}/request-withdrawal/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      toast.success("Withdrawal request submitted successfully!");
      setWithdrawModalOpen(false);
      setWithdrawReason("");
      fetchMyRequests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit withdrawal request.");
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Handle waiver request
  const handleWaiver = (record) => {
    setSelectedRequest(record);
    setWaiverModalOpen(true);
  };

  const submitWaiver = async () => {
    if (!waiverDate || !waiverReason.trim()) {
      toast.warn("Please select date and provide reason.");
      return;
    }

    const payload = {
      new_requested_lwd: dayjs(waiverDate).format("YYYY-MM-DD"),
      reason: waiverReason.trim(),
    };

    try {
      setWaiverLoading(true);
      await axios.post(
        `${backUrl}/api/payroll/resignations/${selectedRequest.id}/request-waiver/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      toast.success("Waiver request submitted successfully!");
      setWaiverModalOpen(false);
      setWaiverDate(null);
      setWaiverReason("");
      fetchMyRequests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit waiver request.");
    } finally {
      setWaiverLoading(false);
    }
  };

  const columns = [
    {
      title: "Submitted On",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-GB") : "-",
    },
    {
      title: "Last Working On",
      dataIndex: "requested_last_working_day",
      key: "requested_last_working_day",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-GB") : "-",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Waiver Requested",
      dataIndex: "is_waiver_requested",
      key: "is_waiver_requested",
      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "200px",
      render: (status) => {
        const color =
          status === "Approved"
            ? "green"
            : status === "Rejected"
              ? "red"
              : "orange";
        return <Text style={{ color }}>{status || "Pending"}</Text>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="link"
            style={{ color: "#0e497b", marginRight: 8 }}
            onClick={() => handleWithdraw(record)}
          >
            Withdraw
          </Button>

        </>
      ),
    },
    {
      title: "Waiver Request",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="link"
            style={{ color: "#0e497b" }}
            onClick={() => handleWaiver(record)}
          >
            Waiver Request
          </Button>

        </>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 600, margin: "50px auto" }}>
      <center>
        <Button
          block
          onClick={openModal}
          style={{
            borderRadius: 6,
            backgroundColor: "#0e497b",
            borderColor: "#0e497b",
            color: "#ffffffff",
            width: '200px'
          }}
        >
          View My Requests
        </Button>
      </center>
      <br />
      <Card
        bordered
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Title level={4} style={{ textAlign: "center", marginBottom: 24 }}>
          Apply for Resignation
        </Title>

        <div style={{ marginBottom: 20 }}>
          <Typography.Text strong>
            Will you serve the full notice period?
          </Typography.Text>
          <Radio.Group
            onChange={(e) => setWillServeNotice(e.target.value)}
            value={willServeNotice}
            style={{ display: "block", marginTop: 8 }}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {
          willServeNotice === "yes" && (
            <>
              <Text strong>Requested Last Working Day:</Text>
              <DatePicker
                style={{ width: "100%", marginTop: 8 }}
                value={waiverDate}
                format="DD/MM/YYYY"
                disabled   // 🔒 not editable
              />
            </>
          )
        }

        {
          willServeNotice === 'no' && (
            <>
              <Text strong>New Requested Last Working Day:</Text>
              <DatePicker
                style={{ width: "100%", marginTop: 8 }}
                value={waiverDate}
                onChange={(date) => setWaiverDate(date)}
                format="DD/MM/YYYY"
              />
            </>
          )
        }
        <br />


        <div style={{ marginBottom: 20, marginTop: 15 }}>
          <Typography.Text strong>Reason for Resignation</Typography.Text>
          <TextArea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please specify your reason..."
          />
        </div>

        <Button
          type="primary"
          block
          loading={loading}
          onClick={handleSubmit}
          style={{
            borderRadius: 6,
            backgroundColor: "#0e497b",
            borderColor: "#0e497b",
            marginBottom: 10,
          }}
        >
          Submit Resignation
        </Button>
      </Card>

      {/* Modal for viewing previous resignations */}
      <Modal
        title="My Resignation Requests"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={1300}
      >
        {tableLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={records}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            bordered
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      backgroundColor: "#0e497b",
                      color: "white",
                      marginTop: "20px",
                    }}
                  >
                    {props.children}
                  </th>
                ),
              },
            }}
          />
        )}
      </Modal>

      {/* Withdraw modal */}
      <Modal
        title="Withdraw Resignation"
        open={withdrawModalOpen}
        onCancel={() => setWithdrawModalOpen(false)}
        onOk={submitWithdraw}
        okText="Submit"
        confirmLoading={withdrawLoading}
      >
        <TextArea
          rows={4}
          placeholder="Reason for withdrawal..."
          value={withdrawReason}
          onChange={(e) => setWithdrawReason(e.target.value)}
        />
      </Modal>

      {/* Waiver modal */}
      <Modal
        title="Request Waiver"
        open={waiverModalOpen}
        onCancel={() => setWaiverModalOpen(false)}
        onOk={submitWaiver}
        okText="Submit"
        confirmLoading={waiverLoading}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>New Requested Last Working Day:</Text>
          <DatePicker
            style={{ width: "100%", marginTop: 8 }}
            value={waiverDate}
            onChange={(date) => setWaiverDate(date)}
            format="DD/MM/YYYY"
          />
        </div>
        <div>
          <Text strong>Reason:</Text>
          <TextArea
            rows={4}
            placeholder="Reason for waiver..."
            value={waiverReason}
            onChange={(e) => setWaiverReason(e.target.value)}
          />
        </div>
      </Modal>

      <ToastContainer
        position="top-center"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
}
